/**
 * Booking admin hooks — schedule templates + booking list.
 *
 * Uses direct fetch (with credentials) rather than the Hono RPC client
 * because the booking router is mounted at a nested path that the type
 * inference doesn't always trace cleanly. Adopting RPC types here is a
 * follow-up.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

async function adminFetch(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export const BOOKING_TEMPLATES_KEY = ['admin', 'booking', 'templates'] as const;
export const BOOKING_LIST_KEY = ['admin', 'booking', 'list'] as const;

export interface AvailabilityTemplate {
  id?: string;
  doctor_id: string;
  weekday: number; // 0=mon..6=sun
  start_time: string; // HH:MM
  end_time: string;
  slot_minutes: number;
  service_slugs: string[] | null;
  buffer_minutes: number;
}

export interface BookingRow {
  id: string;
  doctor_id: string;
  service_slug: string | null;
  start_at: number;
  end_at: number;
  patient_name: string;
  patient_phone: string;
  patient_email: string | null;
  notes: string | null;
  state: 'confirmed' | 'cancelled' | 'no_show' | 'completed';
  created_at: number;
}

export function useTemplates() {
  return useQuery({
    queryKey: BOOKING_TEMPLATES_KEY,
    queryFn: async (): Promise<AvailabilityTemplate[]> => {
      const res = await adminFetch('/api/admin/booking/templates');
      if (!res.ok) throw new Error('failed_to_load_templates');
      const json = (await res.json()) as { templates: AvailabilityTemplate[] };
      return json.templates;
    },
    staleTime: 30_000,
  });
}

export function useSaveTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templates: AvailabilityTemplate[]) => {
      const res = await adminFetch('/api/admin/booking/templates', {
        method: 'PUT',
        body: JSON.stringify({ templates }),
      });
      if (!res.ok) throw new Error('save_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BOOKING_TEMPLATES_KEY }),
  });
}

export function useBookings() {
  return useQuery({
    queryKey: BOOKING_LIST_KEY,
    queryFn: async (): Promise<BookingRow[]> => {
      const res = await adminFetch('/api/admin/booking/list');
      if (!res.ok) throw new Error('failed_to_load_bookings');
      const json = (await res.json()) as { bookings: BookingRow[] };
      return json.bookings;
    },
  });
}

export function useUpdateBookingState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      state: 'cancelled' | 'no_show' | 'completed';
      reason?: string;
    }) => {
      const res = await adminFetch(`/api/admin/booking/${encodeURIComponent(input.id)}/state`, {
        method: 'POST',
        body: JSON.stringify({ state: input.state, reason: input.reason }),
      });
      if (!res.ok) throw new Error('update_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BOOKING_LIST_KEY }),
  });
}

export const ICAL_TOKENS_KEY = ['admin', 'booking', 'ical-tokens'] as const;

export interface IcalToken {
  doctor_id: string;
  token: string;
  created_at: number;
  last_used_at: number | null;
}

export function useIcalTokens() {
  return useQuery({
    queryKey: ICAL_TOKENS_KEY,
    queryFn: async (): Promise<IcalToken[]> => {
      const res = await adminFetch('/api/admin/booking/ical-tokens');
      if (!res.ok) throw new Error('failed_to_load_ical_tokens');
      const json = (await res.json()) as { tokens: IcalToken[] };
      return json.tokens;
    },
    staleTime: 60_000,
  });
}

export function useRotateIcalToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doctorId: string): Promise<IcalToken> => {
      const res = await adminFetch(
        `/api/admin/booking/ical-tokens/${encodeURIComponent(doctorId)}`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error('rotate_failed');
      const j = (await res.json()) as { doctor_id: string; token: string };
      return {
        doctor_id: j.doctor_id,
        token: j.token,
        created_at: Math.floor(Date.now() / 1000),
        last_used_at: null,
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ICAL_TOKENS_KEY }),
  });
}

export function useReassignBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; new_doctor_id: string; reason?: string }) => {
      const res = await adminFetch(
        `/api/admin/booking/${encodeURIComponent(input.id)}/reassign`,
        {
          method: 'POST',
          body: JSON.stringify({ new_doctor_id: input.new_doctor_id, reason: input.reason }),
        }
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'reassign_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BOOKING_LIST_KEY }),
  });
}

export function useRevokeIcalToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doctorId: string) => {
      const res = await adminFetch(
        `/api/admin/booking/ical-tokens/${encodeURIComponent(doctorId)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('revoke_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ICAL_TOKENS_KEY }),
  });
}
